import React from 'react';
import { History, AlertCircle, RefreshCw, ArrowRight } from 'lucide-react';
import { useCarrierStatusHistory } from '../../hooks/useCarrierStatusHistory';
import type { StatusHistoryEvent } from '../../types/statusHistory';

export interface StatusHistoryTimelineProps {
  dotNumber: number;
}

/** Human-readable labels for the tracked carrier_status_history fields. */
const FIELD_LABELS: Record<string, string> = {
  operating_status: 'Operating Status',
  safety_rating: 'Safety Rating',
  out_of_service: 'Out of Service',
  allowed_to_operate: 'Allowed to Operate',
  authority_common: 'Common Authority',
  authority_contract: 'Contract Authority',
  authority_broker: 'Broker Authority',
};

/** Adverse values per field (mirrors ADVERSE_STATUS_VALUES on the backend). */
const ADVERSE_VALUES: Record<string, string[]> = {
  operating_status: ['I'],
  safety_rating: ['C', 'U'],
  out_of_service: ['Y'],
  allowed_to_operate: ['N'],
  authority_common: ['REVOKED', 'SUSPENDED'],
  authority_contract: ['REVOKED', 'SUSPENDED'],
  authority_broker: ['REVOKED', 'SUSPENDED'],
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/_/g, ' ');
}

function isAdverse(field: string, value: string | null): boolean {
  if (value === null) return false;
  return (ADVERSE_VALUES[field] ?? []).includes(value);
}

function formatDetectedAt(detectedAt: string): string {
  const date = new Date(detectedAt);
  if (Number.isNaN(date.getTime())) return detectedAt;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatValue(value: string | null): string {
  return value === null || value === '' ? '—' : value;
}

const TimelineEvent: React.FC<{ event: StatusHistoryEvent; isLast: boolean }> = ({
  event,
  isLast,
}) => {
  const adverse = isAdverse(event.field, event.newValue);

  return (
    <li className="relative pl-8 pb-6">
      {/* Connector line */}
      {!isLast && (
        <span
          className="absolute left-[7px] top-5 bottom-0 w-px bg-gray-200"
          aria-hidden="true"
        />
      )}
      {/* Node dot */}
      <span
        className={`absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 ${
          adverse
            ? 'bg-red-100 border-red-500'
            : 'bg-green-100 border-green-500'
        }`}
        aria-hidden="true"
      />

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-sm font-semibold text-gray-900">
            {fieldLabel(event.field)}
          </span>
          <span className="text-xs text-gray-500">
            {formatDetectedAt(event.detectedAt)}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
              isAdverse(event.field, event.oldValue)
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {formatValue(event.oldValue)}
          </span>
          <ArrowRight size={14} className="text-gray-400" aria-hidden="true" />
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
              adverse
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}
          >
            {formatValue(event.newValue)}
          </span>
        </div>

        <div className="text-xs text-gray-600">
          Source: <span className="font-medium">{event.source}</span>
          {event.effectiveDate && (
            <span className="text-gray-500"> · effective {event.effectiveDate}</span>
          )}
        </div>
      </div>
    </li>
  );
};

const LoadingSkeleton: React.FC = () => (
  <ul className="animate-pulse">
    {[0, 1, 2].map((i) => (
      <li key={i} className="relative pl-8 pb-6">
        {i < 2 && (
          <span className="absolute left-[7px] top-5 bottom-0 w-px bg-gray-200" aria-hidden="true" />
        )}
        <span className="absolute left-0 top-1.5 h-4 w-4 rounded-full bg-gray-200" aria-hidden="true" />
        <div className="flex flex-col gap-2">
          <div className="h-4 w-40 rounded bg-gray-200" />
          <div className="h-4 w-56 rounded bg-gray-200" />
          <div className="h-3 w-32 rounded bg-gray-100" />
        </div>
      </li>
    ))}
  </ul>
);

/**
 * Vertical timeline of a carrier's recorded authority/status changes
 * (operating status, safety rating, OOS state, allowed-to-operate, and the
 * three authority fields), backed by GET /api/carriers/:dotNumber/status-history.
 */
const StatusHistoryTimeline: React.FC<StatusHistoryTimelineProps> = ({ dotNumber }) => {
  const { data, total, isLoading, error, refetch } = useCarrierStatusHistory(dotNumber, {
    days: 3650,
    limit: 200,
  });

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <History size={20} className="text-gray-700" aria-hidden="true" />
          <h3 className="text-xl font-semibold text-gray-900">
            Authority &amp; Status History
          </h3>
        </div>
        {!isLoading && !error && (
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md px-2.5 py-1.5 bg-white"
            aria-label="Refresh status history"
          >
            <RefreshCw size={14} aria-hidden="true" />
            Refresh
          </button>
        )}
      </div>

      {isLoading && <LoadingSkeleton />}

      {!isLoading && error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-600 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-700">
              Unable to load status history
            </p>
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-red-700 underline"
            >
              <RefreshCw size={14} aria-hidden="true" />
              Try again
            </button>
          </div>
        </div>
      )}

      {!isLoading && !error && data.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <History size={32} className="text-gray-300" aria-hidden="true" />
          <p className="text-base text-gray-700 font-medium">
            No recorded status changes
          </p>
          <p className="text-sm text-gray-500">
            Authority and status changes will appear here once they are recorded
            for this carrier.
          </p>
        </div>
      )}

      {!isLoading && !error && data.length > 0 && (
        <>
          <p className="text-sm text-gray-600 mb-4">
            {total} recorded change{total === 1 ? '' : 's'} (newest first)
          </p>
          <ul>
            {data.map((event, index) => (
              <TimelineEvent
                key={event.id}
                event={event}
                isLast={index === data.length - 1}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default StatusHistoryTimeline;
